import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Bell, Trash2, AlertTriangle, Plus, Phone, UserMinus, Loader2 } from 'lucide-react';
import ConsentTemplateEditor from './ConsentTemplateEditor';

export default function AdminSettings() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [recipients, setRecipients] = useState([]);
  const [newRec, setNewRec] = useState({ name: '', phone: '', role: 'executive_director', notify_on_crisis: true, notify_on_incident: true });
  const [wipePreview, setWipePreview] = useState(null);
  const [wiping, setWiping] = useState(false);

  const load = async () => {
    try {
      const [recRes, wpRes] = await Promise.all([
        api.get('/crisis/recipients'),
        api.post('/admin/wipe-demo-data'),
      ]);
      setRecipients(recRes.data || []);
      setWipePreview(wpRes.data?.will_delete || null);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const addRecipient = async () => {
    if (!newRec.name || !newRec.phone) return toast.error('Name and phone are required');
    try {
      await api.post('/crisis/recipients', newRec);
      toast.success('Recipient added');
      setNewRec({ name: '', phone: '', role: 'executive_director', notify_on_crisis: true, notify_on_incident: true });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const removeRecipient = async (id) => {
    try {
      await api.delete(`/crisis/recipients/${id}`);
      toast.success('Removed');
      load();
    } catch {
      toast.error('Failed');
    }
  };

  const runWipe = async () => {
    setWiping(true);
    try {
      const res = await api.post('/admin/wipe-demo-data?confirm=YES_WIPE');
      toast.success(`Wiped ${Object.values(res.data.deleted || {}).reduce((a, b) => a + b, 0)} demo records`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setWiping(false);
  };

  return (
    <div className="space-y-6">
      {/* Crisis Recipients */}
      <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-rose-400" data-testid="crisis-recipients-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Bell size={16} className="text-rose-600" /> Crisis Alert Recipients
          </CardTitle>
          <p className="text-xs text-muted-foreground">When a participant presses the panic button, these people get texted (Twilio) + an in-app notification.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {recipients.length === 0 ? (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
              <span>No recipients configured yet. <strong>Crisis alerts will only show the 988 button</strong> until you add at least one contact.</span>
            </div>
          ) : (
            <div className="space-y-2">
              {recipients.map(r => (
                <div key={r.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200">
                  <div className="w-8 h-8 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center text-xs font-bold">{r.name?.[0] || '?'}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{r.name}</p>
                    <p className="text-[11px] text-slate-500 flex items-center gap-1.5"><Phone size={9} /> {r.phone}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px]">{r.role?.replace('_', ' ')}</Badge>
                  <Button onClick={() => removeRecipient(r.id)} variant="ghost" size="icon" className="h-7 w-7 text-rose-600" data-testid={`remove-recipient-${r.id}`}>
                    <Trash2 size={13} />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="p-3 bg-slate-50 rounded-xl space-y-2">
            <p className="text-xs font-semibold">Add Recipient</p>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Name" value={newRec.name} onChange={(e) => setNewRec({ ...newRec, name: e.target.value })} className="h-9 text-sm bg-white" data-testid="new-rec-name" />
              <Input placeholder="+15551234567" value={newRec.phone} onChange={(e) => setNewRec({ ...newRec, phone: e.target.value })} className="h-9 text-sm bg-white" data-testid="new-rec-phone" />
            </div>
            <Select value={newRec.role} onValueChange={(v) => setNewRec({ ...newRec, role: v })}>
              <SelectTrigger className="h-9 text-sm bg-white" data-testid="new-rec-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="executive_director">Executive Director</SelectItem>
                <SelectItem value="on_call_peer">On-Call Peer Specialist</SelectItem>
                <SelectItem value="coordinator">Program Coordinator</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={addRecipient} className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-full gap-1" data-testid="add-recipient-btn">
              <Plus size={14} /> Add Recipient
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Demo Data Wipe — Danger Zone */}
      <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-red-400" data-testid="danger-zone">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2 text-red-700">
            <UserMinus size={16} /> Danger Zone
          </CardTitle>
          <p className="text-xs text-muted-foreground">One-time actions for production launch.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {wipePreview && (
            <div className="p-3 bg-red-50/60 border border-red-200 rounded-xl text-xs space-y-1">
              <p className="font-semibold text-red-800 mb-1">Demo records currently on this server:</p>
              <ul className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-red-700">
                <li>Demo accounts: <strong>{wipePreview.users_with_demo_emails}</strong></li>
                <li>Test prefix users: <strong>{wipePreview.users_with_test_prefix}</strong></li>
                <li>Test session docs: <strong>{wipePreview.session_docs_with_test_prefix}</strong></li>
                <li>Class sessions: <strong>{wipePreview.class_sessions}</strong></li>
                <li>Class reflections: <strong>{wipePreview.class_reflections}</strong></li>
                <li>Activity log: <strong>{wipePreview.activity_log_entries}</strong></li>
              </ul>
            </div>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="rounded-full gap-1.5" data-testid="wipe-demo-btn" disabled={!wipePreview}>
                <Trash2 size={14} /> Wipe All Demo Data
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Permanently delete demo data?</AlertDialogTitle>
                <AlertDialogDescription>
                  This wipes all demo seed accounts (Jordan Lee, James Wilson, etc.) and their associated data across 16+ collections.
                  Your owner account (is_owner=true) and any real participants you've added will be preserved.
                  <br /><br />
                  <strong className="text-red-700">This cannot be undone.</strong>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={runWipe} className="bg-red-600 hover:bg-red-700" data-testid="confirm-wipe-btn">
                  {wiping ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
                  Yes, Wipe Demo Data
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>

      {/* Consent / Privacy / TOS Template Editor */}
      <ConsentTemplateEditor />
    </div>
  );
}
